import classnames from 'classnames';
import { format as formatDate, isValid as isValidDate } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { selfServiceApiClient } from 'config/selfServiceApi';

const dateFormat = 'do MMMM yyyy';
const dateTimeFormat = 'do MMMM yyyy HH:mm';

export const displayDate = (
  timestamp,
  defaultValue = '-',
  format = dateFormat,
  useUTC = false,
) => {
  if (!timestamp) {
    return defaultValue;
  }

  let dateString = timestamp;

  // If timestamp is a string and lacks a timezone, assume UTC by appending 'Z'
  if (typeof timestamp === 'string' && !timestamp.match(/([+-]\d{2}:\d{2}|Z)$/)) {
    dateString += 'Z';
  }

  const date = new Date(dateString);

  if (!isValidDate(date)) {
    console.error('Invalid date:', timestamp);
    return defaultValue;
  }

  if (useUTC) {
    try {
      return formatInTimeZone(date, 'UTC', format);
    } catch (error) {
      console.error('Invalid UTC date format:', timestamp, error);
      return defaultValue;
    }
  }
  
  return formatDate(date, format);
};

export const displayISODateTime = (dateInput, showTime = false, hideMidnight = false) => {
  let date;

  if (typeof dateInput === 'string') {
    const isoDateTimeParts = dateInput.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/);

    if (isoDateTimeParts) {
      date = new Date(isoDateTimeParts[0]);
    }
  } else if (typeof dateInput === 'object' && dateInput.timestamp && dateInput.zone) {
    date = new Date(dateInput.timestamp);
  }

  if (isValidDate(date)) {
    if (showTime) {
      const formatted = formatDate(date, dateTimeFormat);

      // Hide time if it's exactly midnight
      if (hideMidnight && formatted.endsWith('00:00')) {
        return formatDate(date, dateFormat);
      }

      return formatted;
    }

    return formatDate(date, dateFormat);
  }

  return '-';
};

export const displayGMTDate = (
  timestamp,
  defaultValue = '-',
  format = dateFormat,
) => {
  if (!timestamp) {
    return defaultValue;
  }

  if (!isValidDate(new Date(timestamp))) {
    return defaultValue;
  }

  const localDate = new Date(timestamp);
  const gmtDate = new Date(
    localDate.getUTCFullYear(),
    localDate.getUTCMonth(),
    localDate.getUTCDate(),
  );

  return formatDate(gmtDate, format);
};

export const displayTime = (
  timestamp,
  defaultValue = '-',
  format = 'HH:mm',
) => {
  return displayDate(timestamp, defaultValue, format, true);
};

export const diffInHoursAndMinutes = (
  laterDateTimestamp,
  earlierDateTimestamp,
  defaultValue = '',
) => {
  const laterDate = new Date(laterDateTimestamp);
  const earlierDate = new Date(earlierDateTimestamp);

  if (
    Number.isNaN(laterDate.getTime()) ||
    Number.isNaN(earlierDate.getTime())
  ) {
    return defaultValue;
  }

  const timeDifferenceInMilliseconds = laterDate - earlierDate;
  const timeDifferenceInMinutes = timeDifferenceInMilliseconds / (1000 * 60);

  const hours = Math.floor(timeDifferenceInMinutes / 60);
  const minutes = Math.round(timeDifferenceInMinutes % 60);

  const hoursLabel = hours > 1 ? 'hrs' : 'hr';
  const minutesLabel = minutes > 1 ? 'mins' : 'min';

  let result = '';
  if (hours > 0) {
    result += `${hours} ${hoursLabel} `;
  }
  if (minutes > 0) {
    result += `${minutes} ${minutesLabel}`;
  }

  return result.trim();
};

export const getStringValue = (value = '', defaultValue = '-') => {
  const stringValue = value || defaultValue;

  return `${stringValue}`.trim();
};

export const displayAmount = (amount, currency, defaultValue = '-') => {
  let tempAmount = amount;

  if (tempAmount && `${tempAmount}`.trim() === '-0') {
    tempAmount = 0;
  }

  if (!currency || typeof currency !== 'string') {
    return defaultValue;
  }

  return !Number.isNaN(Number(tempAmount))
    ? tempAmount.toLocaleString('en-GB', {
        style: 'currency',
        currency,
      })
    : defaultValue;
};

export const getPluralForm = (word, count, showCount = false) => {
  const baseWord = `${word || ''}`.trim();
  
  if (typeof count !== 'number') {
    return '';
  }

  const formDisplay = count !== 1 ? `${baseWord}s` : baseWord;
  return showCount ? `${count} ${formDisplay}` : formDisplay;
};

export const removeEmptyFromList = (list = []) => {
  return list.filter((item) => item);
};

export const getFullname = ({
  firstName = '',
  middleName = '',
  lastName = '',
} = {}) => {
  let fullName = firstName;

  fullName += middleName ? ` ${middleName}` : '';
  fullName += lastName ? ` ${lastName}` : '';

  return fullName;
};

export const fetchData = async (url, defaultValue = {}) => {
  try {
    const { data } = await selfServiceApiClient(url, { withCredentials: true });

    return data;
  } catch (error) {
    console.error(error);

    return defaultValue;
  }
};

export const getPrimaryFromList = (list = [], defaultValue = {}) => {
  // Returns the primary and active, else the first result

  if (list?.length < 1) {
    return { data: defaultValue, index: -1 };
  }

  const primaryDataIndex = list.findIndex(
    (item) => item.primary && item.active,
  );

  if (primaryDataIndex !== -1) {
    return { data: list[primaryDataIndex], index: primaryDataIndex };
  }

  return { data: list[0], index: 0 };
};

export const getButtonStyles = (className, secondaryColor) => {
  return classnames(
    `${className} text-[14px] text-center text-sm rounded-3xl px-8 py-2 cursor-pointer font-medium flex justify-center items-center`,
    {
      'text-white': !secondaryColor,
    },
    {
      'bg-white hover:bg-default-hover': secondaryColor,
    },
  );
};

export const slugifyString = (rawString = '') => {
  // Copied from App.Helpers.slugifyString in CRM

  return rawString
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, '');
};

export const getGenderDisplay = (gender) => {
  switch (gender) {
    case 'female':
      return 'Female';
    case 'male':
      return 'Male';
    case 'other':
      return 'Unspecified or other';
    default:
      return '';
  }
};

export const trimString = (stringValue = '') => {
  return stringValue.trim?.() || '';
};

export const generateDropdownOptionsFromTagManagerValue = (
  tagManagerRawData = [],
) => {
  const createdDropdownOptions = {};
  tagManagerRawData?.forEach?.(({ key = '', values = [] }) => {
    if (key) {
      createdDropdownOptions[key] = values?.map?.((value) => ({
        value,
        label: value,
      }));
    }
  });

  return createdDropdownOptions;
};

export const getValue = (value, defaultValue) => {
  return value || defaultValue || null;
};

export const validateFormRequiriedFields = (
  formErrorSetter,
  formFieldsConfig = {},
) => {
  if (!formErrorSetter) {
    return false;
  }

  let isFormValid = true;
  const fields = Object.keys(formFieldsConfig);

  fields?.forEach?.((field) => {
    const { required, value, fieldName, label } = formFieldsConfig[field];

    if (!fieldName) {
      return;
    }

    const REQUIRED_FIELD_ERROR_MSG = `${label || 'This field'} is required.`;

    if (required && (!value || getStringValue(value)?.length < 1)) {
      isFormValid = false;
      formErrorSetter?.(fieldName, REQUIRED_FIELD_ERROR_MSG);
    }
  });

  return isFormValid;
};

export const isWCAGContrastLow = (hex) => {
  if (!hex) return false;

  const r = parseInt(hex.substring(1, 3), 16) / 255;
  const g = parseInt(hex.substring(3, 5), 16) / 255;
  const b = parseInt(hex.substring(5, 7), 16) / 255;

  // Apply WCAG formula for relative luminance
  const gammaCorrect = (c) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;

  const luminance = 0.2126 * gammaCorrect(r) + 0.7152 * gammaCorrect(g) + 0.0722 * gammaCorrect(b);
  
  return luminance > 0.5;
};
